/**
 * SignupCaptcha — custom in-house behavioural CAPTCHA modal.
 *
 * Shows AFTER the user submits the signup form. It blocks the
 * actual /api/auth/register call until:
 *   1. The user solves a randomly-chosen challenge (one of: ordered
 *      colour clicks · drag-to-match · word math).
 *   2. The browser's mouse / focus / timing telemetry passes the
 *      backend's behavioural score (≥ 4 of 6 heuristics).
 *
 * On success it resolves with a single-use `captcha_token` that the
 * parent passes straight to /api/auth/register. On failure the
 * server may issue a NEW challenge, or return 403 with the user-
 * spec'd generic message ("Due to security concerns…"). The block
 * timeline is INTENTIONALLY never shown to the user.
 *
 * Browser fingerprint is computed once and persisted in localStorage
 * under `__uplift_fp__`; this is the same value sent to all 3 server
 * endpoints (challenge / verify / register) so the escalation system
 * tracks email + IP + fingerprint as the user spec requires.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import SignupWizardShell from './SignupWizardShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ────────── Browser fingerprint ──────────

/** Collect a stable, low-PII fingerprint of the device. Combines:
 *  • screen resolution + colour depth
 *  • language list
 *  • timezone offset
 *  • canvas paint hash
 *  • a persistent random salt stored in localStorage
 *  None of these are tied to a real identity but together they're
 *  stable enough to track an attacker rotating emails on the same
 *  device. */
const _getFingerprint = () => {
  try {
    let salt = localStorage.getItem('__uplift_fp_salt__');
    if (!salt) {
      salt = (crypto.randomUUID && crypto.randomUUID()) || (Math.random().toString(36).slice(2));
      localStorage.setItem('__uplift_fp_salt__', salt);
    }
    let canvas = '';
    try {
      const c = document.createElement('canvas');
      c.width = 220; c.height = 30;
      const ctx = c.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = "14px 'Arial'";
      ctx.fillStyle = '#f60'; ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = '#069'; ctx.fillText('the.uplift', 2, 15);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'; ctx.fillText('the.uplift', 4, 17);
      canvas = c.toDataURL();
    } catch (_e) { /* canvas blocked = still OK */ }

    const parts = [
      navigator.userAgent || '',
      `${screen.width}x${screen.height}x${screen.colorDepth}`,
      (navigator.languages || [navigator.language || '']).join(','),
      String(new Date().getTimezoneOffset()),
      String(navigator.hardwareConcurrency || 0),
      String(navigator.deviceMemory || 0),
      canvas.slice(-100),
      salt,
    ];
    return parts.join('|');
  } catch (_e) {
    return localStorage.getItem('__uplift_fp_salt__') || 'unknown';
  }
};

const FINGERPRINT = _getFingerprint();
export const getFingerprint = () => FINGERPRINT;

// ────────── Telemetry collector ──────────

const useTelemetry = (active, formMountedAt) => {
  const samplesRef = useRef([]);
  const focusRef = useRef(0);
  useEffect(() => {
    if (!active) return undefined;
    const onMove = (e) => {
      const t = performance.now();
      samplesRef.current.push({ x: e.clientX, y: e.clientY, t });
      if (samplesRef.current.length > 200) samplesRef.current = samplesRef.current.slice(-200);
    };
    const onTouch = (e) => {
      const tch = e.touches && e.touches[0];
      if (!tch) return;
      const t = performance.now();
      samplesRef.current.push({ x: tch.clientX, y: tch.clientY, t });
      if (samplesRef.current.length > 200) samplesRef.current = samplesRef.current.slice(-200);
    };
    const onFocus = () => { focusRef.current += 1; };
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('touchmove', onTouch, { passive: true });
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onTouch);
      window.removeEventListener('focus', onFocus);
    };
  }, [active]);

  const snapshot = (challengeStartedAt) => ({
    elapsed_ms: Math.round(performance.now() - (challengeStartedAt || 0)),
    form_fill_ms: formMountedAt ? Math.round(performance.now() - formMountedAt) : 0,
    mouse_samples: samplesRef.current.slice(-60),
    focus_events: focusRef.current,
    user_agent: navigator.userAgent || '',
    webdriver: !!navigator.webdriver,
  });

  return snapshot;
};

// ────────── Individual challenge UIs ──────────

const ColorBubble = ({ color, onClick, dim, testId }) => {
  const cls = {
    red: 'bg-red-500', green: 'bg-green-500', blue: 'bg-blue-500',
    yellow: 'bg-yellow-400', purple: 'bg-purple-500', orange: 'bg-orange-500',
  }[color] || 'bg-gray-400';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={dim}
      data-testid={testId}
      className={`w-14 h-14 rounded-full ${cls} ${dim ? 'opacity-30' : 'hover:scale-110'} transition-transform shadow-md`}
      aria-label={color}
    />
  );
};

const OrderedClicksChallenge = ({ payload, onAnswer }) => {
  const [clicked, setClicked] = useState([]);
  const order = payload.order || [];
  const targets = payload.targets || [];
  const remaining = order.filter((c) => !clicked.includes(c));
  const next = remaining[0];

  const handle = (color) => {
    if (clicked.includes(color)) return;
    if (color !== next) {
      onAnswer([...clicked, color]);
      return;
    }
    const upd = [...clicked, color];
    setClicked(upd);
    if (upd.length === order.length) onAnswer(upd);
  };

  return (
    <div className="space-y-4" data-testid="captcha-ordered-clicks">
      <p className="text-sm text-center font-semibold text-slate-900">
        Click the dots in this order:
      </p>
      <div className="flex justify-center gap-2 flex-wrap">
        {order.map((c, i) => (
          <span
            key={`o-${i}`}
            className={`text-xs px-2 py-1 rounded-full font-semibold ${clicked.includes(c) ? 'bg-[#586D52] text-white' : 'bg-slate-100 text-slate-700'}`}
          >
            {i + 1}. {c}
          </span>
        ))}
      </div>
      <div className="flex justify-center gap-3 flex-wrap pt-2">
        {targets.map((c, i) => (
          <ColorBubble
            key={`b-${i}`}
            color={c}
            dim={clicked.includes(c)}
            testId={`captcha-bubble-${c}`}
            onClick={() => handle(c)}
          />
        ))}
      </div>
    </div>
  );
};

const DragToMatchChallenge = ({ payload, onAnswer }) => {
  const [pos, setPos] = useState(0);
  const target = payload.target || 50;
  return (
    <div className="space-y-3" data-testid="captcha-drag-to-match">
      <p className="text-sm text-center font-semibold text-slate-900">
        Drag the slider until the marker lines up with the target.
      </p>
      <div className="relative h-10 mx-2">
        <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-2 bg-slate-200 rounded-full" />
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-1 h-6 bg-red-500"
          style={{ left: `${target}%` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-[#586D52] shadow-lg"
          style={{ left: `${pos}%` }}
        />
      </div>
      <input
        type="range"
        min="0"
        max="100"
        value={pos}
        onChange={(e) => setPos(parseInt(e.target.value, 10))}
        className="w-full"
        data-testid="captcha-drag-slider"
      />
      <Button
        type="button"
        className="w-full bg-[#586D52] hover:bg-[#465A41] text-white h-11"
        onClick={() => onAnswer(pos)}
        data-testid="captcha-drag-submit"
      >
        Submit
      </Button>
    </div>
  );
};

const WordMathChallenge = ({ payload, onAnswer }) => {
  const [val, setVal] = useState('');
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => { e.preventDefault(); onAnswer(parseInt(val, 10)); }}
      data-testid="captcha-word-math"
    >
      <p className="text-sm text-center font-semibold text-slate-900">{payload.prompt}</p>
      <Input
        type="number"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        autoFocus
        placeholder="Answer"
        className="text-center text-lg bg-white text-slate-900"
        data-testid="captcha-math-input"
      />
      <Button type="submit" className="w-full bg-[#586D52] hover:bg-[#465A41] text-white h-11" data-testid="captcha-math-submit">Submit</Button>
    </form>
  );
};

// ────────── Top-level modal ──────────

export default function SignupCaptcha({ open, onClose, onPass, email, formMountedAt }) {
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(false);
  const [issuedAt, setIssuedAt] = useState(0);
  const [attempt, setAttempt] = useState(1);          // 1-indexed attempt counter for the header
  const [maxAttempts, setMaxAttempts] = useState(3);
  const passedRef = useRef(false);                      // true once a valid captcha_token is in flight
  const snapshot = useTelemetry(open, formMountedAt);
  const fingerprint = useMemo(() => getFingerprint(), []);

  // iter 98 — when the user closes the modal WITHOUT having
  // successfully solved the challenge, fire `/security/signup-abandon`
  // so the server records it as a failed attempt. This prevents
  // bots from rotating through challenge kinds (open → see math →
  // close → reopen → see slider → solve) at zero cost.
  const fireAbandon = async () => {
    if (passedRef.current) return; // already passed → don't re-count
    try {
      await axios.post(`${API}/security/signup-abandon`, {
        email,
        fingerprint,
      });
    } catch (_e) { /* best-effort */ }
  };

  // Issue (or re-issue) a challenge on open / after a wrong answer.
  const issue = async () => {
    setLoading(true);
    setChallenge(null);
    try {
      const { data } = await axios.post(`${API}/security/signup-challenge`, {
        email,
        fingerprint,
      });
      setChallenge(data);
      setIssuedAt(performance.now());
      // Server returns the count of failed attempts at the current
      // escalation level (0-indexed). Display "Attempt N/3" where
      // N = attempts_in_level + 1 (this is the in-flight attempt).
      const max = Number.isFinite(data?.max_attempts_per_level) ? data.max_attempts_per_level : 3;
      const used = Number.isFinite(data?.attempts_in_level) ? data.attempts_in_level : 0;
      setMaxAttempts(max);
      setAttempt(Math.min(max, used + 1));
    } catch (e) {
      const detail = e.response?.data?.detail || 'Could not start security check';
      toast.error(detail);
      passedRef.current = true; // server errored — don't double-count abandon
      onClose(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      passedRef.current = false;
      issue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submitAnswer = async (answer) => {
    if (!challenge) return;
    setLoading(true);
    try {
      const tele = snapshot(issuedAt);
      const { data } = await axios.post(`${API}/security/signup-verify`, {
        challenge_id: challenge.challenge_id,
        answer,
        telemetry: tele,
        email,
        fingerprint,
      });
      if (data?.ok && data?.captcha_token) {
        passedRef.current = true;
        onPass({ captcha_token: data.captcha_token, fingerprint });
        return;
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || 'Verification failed.';
      // 403 → hard-blocked: surface the generic message and bail
      // out (no retry option).
      if (status === 403) {
        toast.error(detail);
        onClose(detail);
        return;
      }
      // Any other failure → re-issue a fresh challenge so the user
      // gets another attempt (escalation is enforced server-side).
      toast.error(detail);
      await issue();
      return;
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  // Wrap onClose so we always fire abandon when appropriate.
  const handleClose = async (reason) => {
    await fireAbandon();
    onClose(reason);
  };

  return (
    <SignupWizardShell
      open={open}
      step={1}
      icon={<Shield className="w-6 h-6" />}
      onClose={handleClose}
      testId="signup-captcha-modal"
    >
      <div className="flex items-center justify-end mb-3">
        <span
          className="text-[11px] font-medium text-slate-500 tabular-nums"
          data-testid="captcha-attempt-counter"
        >
          Attempt {attempt}/{maxAttempts}
        </span>
      </div>
      {loading && (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      )}
      {!loading && challenge && challenge.kind === 'ordered_clicks' && (
        <OrderedClicksChallenge payload={challenge} onAnswer={submitAnswer} />
      )}
      {!loading && challenge && challenge.kind === 'drag_to_match' && (
        <DragToMatchChallenge payload={challenge} onAnswer={submitAnswer} />
      )}
      {!loading && challenge && challenge.kind === 'word_math' && (
        <WordMathChallenge payload={challenge} onAnswer={submitAnswer} />
      )}
    </SignupWizardShell>
  );
}
